import { calculateTimeRemaining } from "src/utils/timeUtils";
import { DateTime } from "luxon";

/**
 * Gets the translation key for a promotion based on its ID in the config.
 *
 * @function getPromotionTranslationKey
 * @param {Object} promotion - The promotion object containing promotion_id
 * @param {Object} config - The configuration object mapping promotion IDs to their details
 * @returns {string|undefined} The translation key if found, undefined otherwise
 */
export const getPromotionTranslationKey = (promotion, config) => {
  if (!promotion?.promotion_id) {
    console.warn('getPromotionTranslationKey: promotion_id is missing', promotion);
    return undefined;
  }

  const configEntry = config[promotion.promotion_id];

  if (!configEntry?.key) {
    console.warn(`getPromotionTranslationKey: No config entry found for promotion ID: ${promotion.promotion_id}`);
    return undefined;
  }

  return configEntry.key;
};

/**
 * Sorts promotions by their display_order property from low to high.
 *
 * @function sortPromotionsByDisplayOrder
 * @param {Array} promotions - Array of promotion objects
 * @returns {Array} A new array of promotions sorted by display_order
 */
export const sortPromotionsByDisplayOrder = (promotions) => {
  return [...promotions].sort((a, b) => {
    const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

const weekDays = [
  { key: "monday", label: "M" },
  { key: "tuesday", label: "T" },
  { key: "wednesday", label: "W" },
  { key: "thursday", label: "T" },
  { key: "friday", label: "F" },
  { key: "saturday", label: "S" },
  { key: "sunday", label: "S" },
];

/**
 * Returns the eligible days of the week for a given promo draw.
 *
 * @function getEligibleDays
 * @param {Object} draw - The draw object containing boolean flags for each day and optionally an `any_day` flag.
 * @returns {Array<Object>} An array of objects for each day of the week with:
 * - `key`: the lowercase weekday name (e.g., "monday")
 * - `active`: true if the day is eligible
 *
 * @example
 * getEligibleDays({ monday: true, wednesday: true });
 * // returns [
 * //   { key: "monday", active: true },
 * //   { key: "tuesday", active: false },
 * //   ...
 * // ]
 */
export const getEligibleDays = (draw) => {
  return weekDays.map(({ key }) => ({
    key,
    active: draw?.any_day || !!draw?.[key],
  }));
};

// Determines the current status of the promo countdown based on the current time
export function determinePromoStatus(startAt, endAt, drawEnd) {
  const timeUntilStart = calculateTimeRemaining(startAt);
  const timeUntilEnd = calculateTimeRemaining(endAt);
  const timeUntilDraw = calculateTimeRemaining(drawEnd);

  if (timeUntilStart > 0) {
    return "starting"; // Event has not started yet
  } else if (timeUntilEnd > 0) {
    return "during"; // Event started, but late registration is still open
  } else if (timeUntilEnd === 0 && timeUntilDraw > 0) {
    return "drawing"; // Event has ended, but draw is still pending
  } else {
    return "closed"; // Late registration is closed
  }
};

// Adds 5 hours to the given date and returns the new date
// This is used to adjust the time for the promo draw
// to account for the timezone difference
// and ensure the draw is displayed correctly
/**
 * Adds 5 hours to any ISO datetime fields in the draw object.
 *
 * @function addFiveHoursPatch
 * @param {Object} draw - The draw object possibly containing datetime strings.
 * @returns {Object} A new draw object with applicable datetime fields adjusted by +5 hours.
 *
 * @example
 * const adjusted = addFiveHoursPatch({
 *   start_date: "2025-02-22T19:00:00.000-05:00",
 *   end_date: "2025-02-23T19:00:00.000-05:00"
 * });
 * // adjusted.start_date and adjusted.end_date will have 5 hours added
 */
export function addFiveHoursPatch(draw) {
  const patched = {};

  Object.entries(draw).forEach(([key, value]) => {
    if (typeof value === "string" && !isNaN(Date.parse(value))) {
      try {
        const date = DateTime.fromISO(value);
        if (date.isValid) {
          patched[key] = date.plus({ hours: 5 }).toISO();
        } else {
          patched[key] = value;
        }
      } catch {
        patched[key] = value;
      }
    } else {
      patched[key] = value;
    }
  });

  return patched;
}
